import { IconButton, IconButtonProps } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useEffectOnce } from 'react-use';

import { tPortalLib } from '../../locales';
import { AppAdaptedTooltip } from '../app-adapted-tooltip.component';
import { RiveAnimation, RiveAnimationRef } from '../rive-animation';

export interface FavouriteSiteActionProps {
  siteUnitId: string;
  size?: IconButtonProps['size'];
  bg?: string;
  isFavorite?: boolean;
  handleFavouriteClick?: (id: string, favourite: boolean) => void;
}

export const FavoriteSiteActionIcon = (props: FavouriteSiteActionProps) => {
  const riveRef = useRef<RiveAnimationRef>(null);
  const [riveState, setRiveState] = useState<boolean>(false);
  const handleTrigger = (inputName: string) =>
    riveRef.current?.trigger(inputName);

  useEffectOnce(() => {
    setRiveState(props.isFavorite || false);
  });

  useEffect(() => {
    if (!props.isFavorite && riveState) {
      handleTrigger('setActive');
      setRiveState(false);
    }
  }, [props.isFavorite, riveState]);

  const handleClick = (id: string, favourite: boolean) => {
    if (!id) return;

    handleTrigger('setActive');
    setRiveState(!riveState);

    props.handleFavouriteClick?.(id, favourite);
  };

  return (
    <AppAdaptedTooltip
      id={`favourite-tooltip-${props.siteUnitId}`}
      title={
        props.isFavorite
          ? tPortalLib.siteUnits.removeFavourite()
          : tPortalLib.siteUnits.addFavourite()
      }
    >
      <IconButton
        aria-describedby={`favourite-tooltip-${props.siteUnitId}`}
        component="button"
        size={props.size ?? 'small'}
        data-eventid="favourite-site"
        onClick={(event) => {
          event.stopPropagation();
          handleClick?.(props.siteUnitId, !props.isFavorite);
        }}
        sx={{
          backgroundColor: props.bg,
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          '&:hover': {
            backgroundColor: props.bg,
          },
        }}
      >
        <RiveAnimation
          ref={riveRef}
          src={'/rive/favourite.riv'}
          height={24}
          width={24}
          freezeAfterMs={1500}
          initialInputValues={{
            initial: props.isFavorite || false,
          }}
        />
      </IconButton>
    </AppAdaptedTooltip>
  );
};
